import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PUBLIC_BASE } from "../lib/publicBase";
import VerifiedTierBadge from "./VerifiedTierBadge";
import { ShieldCheck, ChevronRight, Share2 } from "lucide-react";
import gaTrack from "../lib/gaTrack";

/**
 * TrustStatusCard — wallet/profile widget that shows the current Verified Tier
 * along with the next step needed to climb the trust ladder. Self-contained:
 * fetches /api/trust/me on mount.
 *
 * Props:
 *   - inline (boolean) compact one-line variant for embedding next to name
 */
export default function TrustStatusCard({ inline = false }) {
  const [trust, setTrust] = useState(null);
  useEffect(() => {
    api.get("/trust/me").then(({ data }) => {
      setTrust(data);
      // Fire GA4 `tier_unlocked` once per browser session per tier. The
      // backend's `lastTierCelebrated` marker is authoritative — this just
      // tells GA so the funnel report works.
      try {
        const tier = data?.tier;
        if (tier && tier !== "none") {
          const key = `ga_tier_unlocked:${tier}`;
          if (!sessionStorage.getItem(key)) {
            sessionStorage.setItem(key, "1");
            gaTrack.tierUnlocked({ tier });
          }
        }
      } catch { /* noop */ }
    }).catch(() => {});
  }, []);
  if (!trust) return null;

  if (inline) {
    return (
      <div className="inline-flex items-center gap-2" data-testid="trust-status-inline">
        <VerifiedTierBadge tier={trust.tier} />
        {trust.nextStep && (
          <span className="text-[11px] text-foreground/55">· {trust.nextStep}</span>
        )}
      </div>
    );
  }

  return (
    <div
      className="border border-secondary/20 bg-card/40 rounded-sm p-4 space-y-3"
      data-testid="trust-status-card"
    >
      <div className="flex items-center justify-between">
        <div className="inline-flex items-center gap-2 text-secondary/85">
          <ShieldCheck size={14} strokeWidth={1.5} />
          <span className="label-mystic">Trust Status</span>
        </div>
        <VerifiedTierBadge tier={trust.tier} />
      </div>

      {trust.tier === "none" && (
        <p className="text-sm text-foreground/70">
          You aren't verified yet. Completing identity verification unlocks the
          Verified Practitioner badge and protects your reputation.
        </p>
      )}

      {trust.nextStep && (
        <a
          href={trust.tier === "none" ? "/cashout" : trust.tier === "practitioner" ? "/profile/me/temple-verify" : "#"}
          className="flex items-center justify-between group text-sm text-foreground/80 hover:text-secondary transition-colors"
          data-testid="trust-next-step"
        >
          <span>{trust.nextStep}</span>
          <ChevronRight size={14} className="text-foreground/40 group-hover:text-secondary" />
        </a>
      )}

      <div className="grid grid-cols-2 gap-2 pt-2 text-[11px]">
        <CriterionRow ok={trust.criteria?.emailOnFile} label="Email" />
        <CriterionRow ok={trust.criteria?.phoneVerified} label="Phone" />
        <CriterionRow ok={trust.criteria?.kycApproved} label="Government ID + Selfie" />
        <CriterionRow ok={trust.criteria?.templeVerified} label="Business / Temple doc" />
        <CriterionRow
          ok={(trust.criteria?.endorsementCount || 0) >= 1}
          label={`Endorsements (${trust.criteria?.endorsementCount || 0})`}
        />
        <CriterionRow
          ok={(trust.criteria?.tenureDays || 0) >= 1000 || trust.criteria?.legacyElder}
          label={`Tenure ${trust.criteria?.tenureDays || 0}d / 1000`}
        />
      </div>

      {trust.tier && trust.tier !== "none" && (
        <ShareTierButton tier={trust.tier} />
      )}
    </div>
  );
}

function ShareTierButton({ tier }) {
  const tierCopy = {
    practitioner: "I just became a Verified Practitioner on Cauldron ✦",
    temple: "I'm a Temple-verified practitioner on Cauldron ♛",
    initiate: "I was endorsed as a Verified Initiate on Cauldron ✠",
    elder: "I just reached Elder tier on Cauldron ♛",
  };
  const text = tierCopy[tier] || `I'm a verified practitioner on Cauldron`;
  const landing = `${PUBLIC_BASE}/auth?mode=register&utm_source=in_app&utm_medium=tier_share&utm_campaign=tier_${tier}`;
  const intent = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text + " " + landing)}`;
  return (
    <a
      href={intent}
      target="_blank"
      rel="noreferrer"
      onClick={() => gaTrack.tierShareClicked({ tier, surface: "in_app" })}
      className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 rounded-sm border border-secondary/40 text-[11px] uppercase tracking-[0.18em] text-secondary hover:bg-secondary/[0.08] transition-colors"
      data-testid="trust-share-tier-btn"
    >
      <Share2 size={11} /> Share my tier on X
    </a>
  );
}

function CriterionRow({ ok, label }) {
  return (
    <div className={`flex items-center gap-1.5 ${ok ? "text-secondary/90" : "text-foreground/50"}`}>
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${ok ? "bg-secondary" : "bg-foreground/25"}`} />
      <span>{label}</span>
    </div>
  );
}
